/**
 * Desired Outcomes List
 *
 * Workspace-level list of Desired Outcomes. From here users can create a
 * new Desired Outcome or open an existing one for editing.
 *
 * A Desired Outcome answers: "How will you know when you have what you want?"
 * It walks through 7 questions to articulate Who, What, Deeper Benefit,
 * Evidence of Success, Obstacles, Ecology, and Steps.
 *
 * Route: /prioritization/desired-outcomes
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  ArrowRight,
  Target,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Circle,
  LayoutList,
  Table2,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from '../prioritization/strategy/StrategyConnectivityDocModal';
import {
  useDesiredOutcomeStore,
  type DesiredOutcome,
} from '../../stores/prioritization/desiredOutcomeStore';
import { getItemCountState } from '../../components/collection/types';

// ── Completeness helpers ───────────────────────────────────────

const REQUIRED_FIELDS: (keyof DesiredOutcome)[] = [
  'who',
  'whatWanted',
  'deeperBenefit',
  'evidenceSufficient',
  'evidenceTargeted',
  'evidenceIdeal',
  'ecology',
];

function completenessOf(outcome: DesiredOutcome): number {
  const filled = REQUIRED_FIELDS.filter((f) => {
    const v = outcome[f];
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    return v != null;
  }).length;
  return Math.round((filled / REQUIRED_FIELDS.length) * 100);
}

// ── About section ──────────────────────────────────────────────

const QUESTIONS = [
  { n: 'Q1', label: 'Who wants it?', body: "Name exactly one Who — one persona or logically unified group. Overloading Who is the #1 failure mode." },
  { n: 'Q2', label: "What's wanted?", body: "A single-sentence, positive statement from the Who's point of view. State what you DO want, not what you don't." },
  { n: 'Q3', label: 'What does it get them?', body: 'The deeper benefit or meta-outcome. Include emotional benefits: confidence, peace of mind, pride. Ask "So that...?" until it stops changing decisions.' },
  { n: 'Q4', label: 'Evidence of success', body: "Present-tense, sensory evidence across three levels: Barely Sufficient, Targeted, and Hopelessly Idealistic. This prevents hallucination — you're creating a shared picture." },
  { n: 'Q5', label: 'Obstacles', body: 'What prevents it now? Categorize by People, Process, Technology, Policy, or Budget. Be specific.' },
  { n: 'Q6', label: 'Ecology check', body: "Who loses? What breaks? What compounds over time? Don't skip this — there are always tradeoffs. Include mitigations." },
  { n: 'Q7', label: 'Steps', body: 'Sequence with dependencies and threshold gates. Look for Step Zero — the hidden precondition that must happen before anything else.' },
];

function AboutDesiredOutcomes() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'problem' | 'questions'>('problem');

  return (
    <div className="mb-6 border border-[var(--green)] dark:border-[var(--green)]/60 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/30 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Target className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
            What is a Desired Outcome? How does it work?
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-[var(--green)]" />
          : <ChevronDown className="h-4 w-4 text-[var(--green)]" />
        }
      </button>

      {open && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border-t border-[var(--green)] dark:border-[var(--green)]/40">
          <div className="px-5 py-4 border-b border-[var(--rule)]">
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed max-w-3xl">
              A <strong>Desired Outcome</strong> is a seven-question structured artifact that answers:
              <em> "How will you know when you have what you want?"</em> It prevents the <strong>rock fetch
              cycle</strong> — the pattern of delivering something, being told it's wrong, clarifying,
              delivering again, still not right — by forcing one specific Who to articulate what they want,
              why it matters, and what sensory evidence of success looks like across three levels.
            </p>
          </div>

          <div className="flex border-b border-[var(--rule)] px-5 pt-3">
            <button
              onClick={() => setTab('problem')}
              className={`pb-2 mr-5 text-sm font-medium border-b-2 transition-colors ${
                tab === 'problem'
                  ? 'border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                  : 'border-transparent text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              Why it matters
            </button>
            <button
              onClick={() => setTab('questions')}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'questions'
                  ? 'border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                  : 'border-transparent text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              The 7 questions
            </button>
          </div>

          {tab === 'problem' && (
            <div className="px-5 py-4 space-y-4 max-w-3xl">
              <div>
                <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">The rock fetch problem</p>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
                  When someone says "tree," one person thinks apple tree, another thinks redwood. Requests fail
                  because they're vague, overloaded with multiple audiences, or stated in negative terms. People
                  hallucinate different pictures of success, leading to endless rework.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">What a Desired Outcome produces</p>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
                  A compact one-page artifact used to align teams before work begins, guide tradeoff decisions
                  during execution, and set threshold-based review gates. The evidence range (Barely Sufficient
                  → Targeted → Hopelessly Idealistic) creates a shared picture that prevents hallucination mismatches.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">The one-Who rule</p>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
                  Overloading Who is the #1 failure mode. If you have two distinct audiences with different
                  needs, create two separate Desired Outcomes. One outcome = one Who = one clear set of evidence.
                </p>
              </div>
            </div>
          )}

          {tab === 'questions' && (
            <div className="px-5 py-4">
              <ol className="space-y-3 max-w-2xl">
                {QUESTIONS.map((q) => (
                  <li key={q.n} className="flex gap-3">
                    <span className="flex-shrink-0 w-8 h-6 rounded bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-mono font-bold flex items-center justify-center mt-0.5">
                      {q.n}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{q.label}</p>
                      <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5 leading-relaxed">{q.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Outcome card ───────────────────────────────────────────────

function OutcomeCard({ outcome }: { outcome: DesiredOutcome }) {
  const pct = completenessOf(outcome);
  const isComplete = pct === 100;

  return (
    <Link
      to={`/prioritization/desired-outcomes/${outcome.id}`}
      className="group block p-5 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:shadow-md hover:border-[var(--green)] dark:hover:border-[var(--green)] transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isComplete
              ? <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
              : <Circle className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            }
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate group-hover:text-[var(--green)] dark:group-hover:text-[var(--text-ter)] transition-colors">
              {outcome.title || 'Untitled'}
            </h3>
          </div>
          {outcome.who && (
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1.5 ml-6">
              <span className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mr-1">Who:</span>
              {outcome.who}
            </p>
          )}
          {outcome.whatWanted && (
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1 ml-6 line-clamp-2 leading-relaxed">
              {outcome.whatWanted}
            </p>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--green)] flex-shrink-0 mt-0.5 transition-colors" />
      </div>

      <div className="mt-3 ml-6 flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--green)] dark:bg-[var(--green)] rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] w-8 text-right tabular-nums">
          {pct}%
        </span>
      </div>
    </Link>
  );
}

// ── Table row ──────────────────────────────────────────────────

function OutcomeTableRow({ outcome }: { outcome: DesiredOutcome }) {
  const pct = completenessOf(outcome);
  return (
    <tr className="border-b border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40 transition-colors">
      <td className="px-4 py-2.5">
        <Link to={`/prioritization/desired-outcomes/${outcome.id}`}
          className="flex items-center gap-2 group">
          {pct === 100
            ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0" />
            : <Circle className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />}
          <span className="text-sm font-medium text-[var(--text-pri)] group-hover:text-[var(--green)] dark:group-hover:text-[var(--text-ter)] transition-colors truncate max-w-xs">
            {outcome.title || 'Untitled'}
          </span>
        </Link>
      </td>
      <td className="px-3 py-2.5 max-w-[180px]">
        <span className="text-xs text-[var(--text-ter)] truncate block">{outcome.who || '—'}</span>
      </td>
      <td className="px-3 py-2.5 w-32">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--green)] dark:bg-[var(--green)] rounded-full" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] text-[var(--text-ter)] w-7 text-right tabular-nums">{pct}%</span>
        </div>
      </td>
      <td className="px-3 py-2.5 w-8 text-right">
        <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
      </td>
    </tr>
  );
}

// ── Main page ──────────────────────────────────────────────────

export default function DesiredOutcomesList() {
  const navigate = useNavigate();
  const { outcomes, isLoading, error, fetchOutcomes, createOutcome } = useDesiredOutcomeStore();
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'table' | null>(null);

  useEffect(() => {
    fetchOutcomes();
  }, [fetchOutcomes]);

  // Objectives link here with ?objectiveId= to show the outcomes under one
  // objective; the list honours it and offers the way back to all of them.
  const [searchParams] = useSearchParams();
  const objectiveId = searchParams.get('objectiveId');
  const visibleOutcomes = objectiveId
    ? outcomes.filter((o) => o.objectiveId === objectiveId)
    : outcomes;

  const countState = getItemCountState(visibleOutcomes.length);
  const effectiveView = viewMode ?? (countState === 'many' ? 'table' : 'cards');

  async function handleCreate() {
    const t = newTitle.trim();
    if (!t) return;
    setIsCreating(true);
    const { outcome, error: createError } = await createOutcome({ title: t });
    setIsCreating(false);
    if (createError || !outcome) return;
    navigate(`/prioritization/desired-outcomes/${outcome.id}`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Target className="h-4 w-4 text-[var(--green)]" />}
        iconBg="bg-[var(--green-dim)] dark:bg-[var(--green)]/30"
        title="Desired Outcomes"
        subtitle="Structured 7-question artifacts — define who wants what, why it matters, and what success looks like"
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="desired_outcomes" />
            {outcomes.length > 0 && (
              <div className="flex items-center border border-[var(--rule)] overflow-hidden">
                <button onClick={() => setViewMode('cards')} title="Card view"
                  className={`p-1.5 transition-colors ${effectiveView === 'cards' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
                  <LayoutList className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setViewMode('table')} title="Table view"
                  className={`p-1.5 transition-colors ${effectiveView === 'table' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}>
                  <Table2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--green)] hover:bg-[var(--green)] text-white text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Desired Outcome
            </button>
          </div>
        }
      />

      <AboutDesiredOutcomes />

      {showNewForm && (
        <div className="mb-2 p-4 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20">
          <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
            Give this a name — the initiative, request, or outcome you're clarifying:
          </p>
          <div className="flex gap-2">
            <input
              autoFocus
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') { setShowNewForm(false); setNewTitle(''); }
              }}
              placeholder="e.g. Monday priorities meeting, Series A fundraise, Product launch readiness"
              className="flex-1 px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || isCreating}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-60 text-white text-sm font-medium transition-colors"
            >
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Create
            </button>
            <button
              onClick={() => { setShowNewForm(false); setNewTitle(''); }}
              className="px-3 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading...
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-[var(--coral)] dark:text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={fetchOutcomes} className="ml-auto underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {objectiveId && (
        <div className="flex items-center gap-3 text-sm text-[var(--text-sec)] border border-[var(--rule)] bg-[var(--surface)] px-4 py-2">
          <span>Showing the {visibleOutcomes.length} outcome{visibleOutcomes.length === 1 ? '' : 's'} linked to this objective.</span>
          <Link to="/prioritization/desired-outcomes" className="underline hover:no-underline">
            Show all
          </Link>
        </div>
      )}

      {!isLoading && !error && outcomes.length > 0 && visibleOutcomes.length === 0 && (
        <div className="p-8 text-center text-sm text-[var(--text-ter)] border border-[var(--rule)] bg-[var(--surface)]">
          No desired outcome is linked to this objective yet.
        </div>
      )}

      {!isLoading && !error && outcomes.length === 0 && (
        <div className="text-center py-16 border border-dashed border-[var(--rule)]">
          <Target className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
          <h3 className="text-base font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
            No Desired Outcomes yet
          </h3>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4 max-w-sm mx-auto">
            Start by naming what you're trying to get clarity on — then answer the 7 questions to define
            who wants it, why it matters, and what success looks like.
          </p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--green)] hover:bg-[var(--green)] text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create your first Desired Outcome
          </button>
        </div>
      )}

      {!isLoading && !error && visibleOutcomes.length > 0 && (
        <>
          {effectiveView === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleOutcomes.map((outcome) => (
                <OutcomeCard key={outcome.id} outcome={outcome} />
              ))}
            </div>
          )}
          {effectiveView === 'table' && (
            <div className="border border-[var(--rule)] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60 border-b border-[var(--rule)]">
                    <th className="px-4 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Outcome</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Who</th>
                    <th className="px-3 py-2 w-32 text-left text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Complete</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {visibleOutcomes.map((outcome) => (
                    <OutcomeTableRow key={outcome.id} outcome={outcome} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
